import { Link } from 'react-router-dom';
import { sx } from '../../ui/designStyles';

/**
 * Finance › Private pay (design "Finance · Private pay", L12274; change log D7-D11): what
 * private-pay families owe, autopay, savings running low with the Medicaid / VA paperwork,
 * who has paid this month and the late payments.
 *
 * No route serves private-pay billing yet (PROPOSED: GET /finance/private-pay?facility=, fed by
 * the building's billing system: PointClickCare TransactCare, Yardi, ALIS, MatrixCare, ECP,
 * Eldermark, or Stripe). Every block says so and names the first step; nothing is estimated.
 */

export interface PrivatePayBoard {
  connected: boolean;
  owedUsd: number | null;
  autopayPct: number | null;
  daysToCollect: number | null;
  plans: number | null;
}

/** Until the private-pay route exists. */
export function privatePayBoard(): PrivatePayBoard {
  return { connected: false, owedUsd: null, autopayPct: null, daysToCollect: null, plans: null };
}

const CARD = 'display:flex;flex-direction:column;gap:4px;padding:12px 14px;border-radius:10px;background:#FBF8F1;border:1px solid rgba(34,39,46,.12);';
const EMPTY = 'padding:12px 16px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.12);font-size:16px;color:#3C454F;line-height:1.45;';

export function PrivatePay({ facName, canAct }: { facName: string; canAct: boolean }) {
  const d = privatePayBoard();
  const stats: [string, string, string][] = [
    ['Private pay owed', '–', 'not connected'],
    ['On autopay', '–', 'not connected'],
    ['Days to collect', '–', 'not connected'],
    ['Payment plans', '–', 'up to 4 payments, no interest'],
  ];
  return (
    <div data-screen-label="Finance · Private pay" data-testid="finance-private-pay" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:14px;min-width:0;padding-bottom:20px;')}>
      <div style={sx('display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;padding:14px 16px;border-radius:12px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
        <span style={sx('flex:1 1 320px;min-width:0;font-size:18px;font-weight:800;line-height:1.4;')}>
          {d.connected ? '' : `Private pay is not connected for ${facName} yet.`}
        </span>
        {canAct && (
          <Link to="/settings?section=integrations" style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}>
            Connect billing
          </Link>
        )}
      </div>
      <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;max-width:90ch;')}>
        First step: connect the billing system your building already uses, in Settings › Integrations › Billing. Statements, payments, autopay and late payments then show here, and families pay from the Family Portal. Aithora never stores card or bank details.
      </span>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:10px;')}>
        {stats.map(([k, v, s]) => (
          <div key={k} style={sx(CARD)}>
            <span style={sx('font-size:14px;font-weight:700;color:#54606C;')}>{k}</span>
            <span style={sx('font-size:24px;font-weight:800;font-variant-numeric:tabular-nums;color:#54606C;')}>{v}</span>
            <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>{s}</span>
          </div>
        ))}
      </div>
      <div style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;')}>
        <span style={sx('font-size:18px;font-weight:800;')}>Savings running low</span>
      </div>
      <div style={sx(EMPTY)}>
        Nobody is listed yet. With billing connected, residents whose savings run low within 12 or 6 months show here, with Start Medicaid / VA paperwork and the veteran question.
      </div>
      <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;margin-top:6px;')}>
        <span style={sx('font-size:18px;font-weight:800;')}>This month’s statements · who has paid</span>
      </div>
      <div style={sx(EMPTY)}>No statements yet. They come from the connected billing system.</div>
      <div style={sx('font-size:18px;font-weight:800;margin-top:6px;')}>Late payments</div>
      <div style={sx(EMPTY)}>No late payments to show.</div>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.5;')}>
        Reminders go out in the building’s name, only to people who agreed to be contacted: payer, then business office, then administrator.
      </div>
    </div>
  );
}
